import React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { CheckCircle2, Minus, Plus, XCircle } from "lucide-react";

export function isStudentsListTable(tableName) {
  const key = String(tableName || "").toLowerCase();
  return key === "students" || key === "ems_students";
}

export function getStudentRowId(row, mainKey, rowIndex = 0) {
  // mainKey must win: this is used as the React key for every table's rows, not just
  // Students. Fee Installments (and others) also carry organization_ems_student_id on
  // each row (for a Receipt prefill), and one student has many installments — checking
  // that field first gave duplicate rows the same key, so React reused stale row state
  // on reorder instead of re-rendering, scrambling the display without touching the data.
  return String(
    row?.[mainKey] ?? row?.organization_ems_student_id ?? row?.id ?? rowIndex
  );
}

export function getStudentAccordionRows(row) {
  const parseAmount = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const n = Number(String(value).replace(/,/g, "").trim());
    return Number.isFinite(n) ? n : 0;
  };
  const formatInr = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const formatShortDate = (value) => {
    const s = String(value ?? "").trim();
    if (!s) return "-";
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return s;
  };

  const admissions = Array.isArray(row?.student_admissions)
    ? row.student_admissions
    : Array.isArray(row?.StudentAdmissions)
      ? row.StudentAdmissions
      : [];
  const installments = Array.isArray(row?.student_installments)
    ? row.student_installments
    : Array.isArray(row?.StudentInstallments)
      ? row.StudentInstallments
      : [];
  const batches = Array.isArray(row?.studnet_batches)
    ? row.studnet_batches
    : Array.isArray(row?.StudnetBatches)
      ? row.StudnetBatches
      : Array.isArray(row?.student_batches)
        ? row.student_batches
        : [];
  const placementList = Array.isArray(row?.placements)
    ? row.placements
    : Array.isArray(row?.Placements)
      ? row.Placements
      : [];
  const certificates = Array.isArray(row?.certficates)
    ? row.certficates
    : Array.isArray(row?.certficate)
      ? row.certficate
      : Array.isArray(row?.Certficate)
        ? row.Certficate
        : row?.certficate && typeof row.certficate === "object"
          ? [row.certficate]
          : [];

  const certificateMatchesAdmission = (cert, admissionId, programId, batchId) => {
    if (!cert || typeof cert !== "object") return false;
    const certAdmissionId = cert?.organization_ems_admission_id;
    if (
      admissionId != null &&
      certAdmissionId != null &&
      String(certAdmissionId) === String(admissionId)
    ) {
      return true;
    }
    const certProgramId = cert?.training_program_id;
    const programMatch =
      programId != null &&
      certProgramId != null &&
      String(certProgramId) === String(programId);
    if (!programMatch) return false;
    const certBatchId = cert?.organization_ems_batch_id;
    if (certBatchId != null && batchId != null) {
      return String(certBatchId) === String(batchId);
    }
    if (certBatchId == null && batchId == null) return true;
    return false;
  };

  return admissions.map((admission, index) => {
    const admissionId = admission?.organization_ems_admission_id;
    const admissionInstallments = installments.filter(
      (ins) =>
        String(ins?.organization_ems_admission_id ?? "") ===
        String(admissionId ?? "")
    );

    const installmentsTotalInr = admissionInstallments.reduce(
      (sum, ins) => sum + parseAmount(ins?.fee_instalment_inr),
      0
    );
    const installmentsPaidInr = admissionInstallments.reduce((sum, ins) => {
      const st = String(ins?.status ?? "").trim().toLowerCase();
      if (st === "paid" || st === "successful") {
        return sum + parseAmount(ins?.fee_instalment_inr);
      }
      return sum;
    }, 0);
    const installmentsPendingInr = admissionInstallments.reduce((sum, ins) => {
      const st = String(ins?.status ?? "").trim().toLowerCase();
      if (st === "paid" || st === "successful") return sum;
      return sum + parseAmount(ins?.fee_instalment_inr);
    }, 0);
    const netFeeInr = (() => {
      const n = parseAmount(admission?.net_fee_amount);
      if (n > 0) return n;
      return installmentsTotalInr;
    })();
    const pendingInr =
      installmentsPendingInr > 0
        ? installmentsPendingInr
        : Math.max(netFeeInr - installmentsPaidInr, 0);

    const batchRecord =
      batches.find(
        (b) =>
          admissionId != null &&
          String(b?.organization_ems_admission_id ?? "") === String(admissionId)
      ) || batches[index];

    const batchName =
      batchRecord?.batch?.batch_name ??
      batchRecord?.batch_name ??
      batchRecord?.organization_ems_batch_id ??
      "-";
    const batchCode =
      batchRecord?.batch?.batch_code ?? batchRecord?.batch_code ?? "-";
    const batchCompletionDate = formatShortDate(
      batchRecord?.completion_date ?? "-"
    );
    const admissionOrEnrollmentDate =
      admission?.admission_date ?? batchRecord?.enrollment_date ?? "-";

    const admissionProgramId =
      admission?.training_program_id ??
      admission?.training_program?.organization_ems_training_program_id ??
      admission?.training_program?.training_program_id ??
      null;
    const batchId =
      batchRecord?.organization_ems_batch_id ??
      batchRecord?.batch?.organization_ems_batch_id ??
      null;
    const hasCertificate = certificates.some((cert) =>
      certificateMatchesAdmission(cert, admissionId, admissionProgramId, batchId)
    );
    const hasPlacement = placementList.length > 0;

    return {
      key: `${String(admissionId ?? "admission")}-${index}`,
      trainingProgram:
        admission?.training_program?.training_program_name ??
        admission?.training_program_name ??
        "-",
      admissionNumber: admission?.admission_number ?? "-",
      admissionOrEnrollmentDate: formatShortDate(admissionOrEnrollmentDate),
      fee: {
        net: formatInr(netFeeInr),
        pending: formatInr(pendingInr),
        pendingRaw: pendingInr,
      },
      batchCode,
      batchName,
      status:
        batchRecord?.batch_status ??
        admission?.admission_status ??
        row?.student_status ??
        "-",
      batchCompletionDate,
      hasCertificate,
      hasPlacement,
    };
  });
}

const GRID_COLUMNS =
  "minmax(200px,2fr) minmax(130px,1.1fr) minmax(130px,1fr) minmax(130px,1fr) minmax(150px,1.3fr) minmax(120px,1fr) minmax(120px,1fr) minmax(130px,1fr)";

export function StudentExpandHeaderCell({ colors }) {
  return (
    <TableCell
      sx={{
        p: 2,
        width: 56,
        minWidth: 56,
        maxWidth: 56,
        textAlign: "center",
        fontWeight: 700,
        fontSize: "14px",
        color: colors.text.primary,
        borderRight: `1px solid ${colors.grey[300]}`,
        backgroundColor: colors.grey[100],
      }}
    >
      +
    </TableCell>
  );
}

export function StudentExpandToggleCell({ expanded, onToggle, colors }) {
  return (
    <TableCell
      sx={{
        p: 1,
        textAlign: "center",
        borderBottom: `1px solid ${colors.grey[200]}`,
        width: 56,
        minWidth: 56,
        maxWidth: 56,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton
        size="small"
        onClick={onToggle}
        sx={{ color: colors.text.primary }}
      >
        {expanded ? <Minus size={16} /> : <Plus size={16} />}
      </IconButton>
    </TableCell>
  );
}

export function StudentAccordionCollapseRow({
  expanded,
  colSpan,
  accordionRows,
  colors,
}) {
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 0,
          px: 2,
          borderBottom: `1px solid ${colors.grey[200]}`,
          backgroundColor: colors.grey[50],
        }}
      >
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ py: 1.5, pl: "56px" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: GRID_COLUMNS,
                gap: 1,
                pr: 1,
                pb: 1,
                fontSize: "12px",
                fontWeight: 700,
                color: colors.text.secondary,
              }}
            >
              <Box>Training Program</Box>
              <Box>Admission Number</Box>
              <Box>Fee (INR)</Box>
              <Box>Pending (INR)</Box>
              <Box>Batch</Box>
              <Box>Status</Box>
              <Box>Certificate</Box>
              <Box>Placement</Box>
            </Box>

            {accordionRows.length === 0 ? (
              <Typography variant="body2" sx={{ px: 1, color: colors.text.secondary }}>
                No admission details available.
              </Typography>
            ) : (
              accordionRows.map((detail) => (
                <Box
                  key={detail.key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_COLUMNS,
                    alignItems: "flex-start",
                    gap: 1,
                    pr: 1,
                    py: 1,
                    fontSize: "13px",
                    borderTop: `1px dashed ${colors.grey[300]}`,
                  }}
                >
                  <Box>{detail.trainingProgram}</Box>
                  <Box sx={{ lineHeight: 1.35 }}>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      {detail.admissionNumber}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: colors.text.secondary }}
                    >
                      {detail.admissionOrEnrollmentDate}
                    </Typography>
                  </Box>
                  <Box>{detail?.fee?.net ?? "0.00"}</Box>
                  <Box
                    sx={{
                      color:
                        Number(detail?.fee?.pendingRaw ?? 0) > 0
                          ? colors.error
                          : colors.text.primary,
                      fontWeight: 600,
                    }}
                  >
                    {detail?.fee?.pending ?? "0.00"}
                  </Box>
                  <Box sx={{ lineHeight: 1.35 }}>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      {detail.batchCode ?? "-"}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: colors.text.secondary }}
                    >
                      {detail.batchName ?? "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ lineHeight: 1.35 }}>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      {detail.status}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: colors.text.secondary }}
                    >
                      {detail.batchCompletionDate}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", pt: "2px" }}>
                    {detail.hasCertificate ? (
                      <CheckCircle2 size={18} color={colors.success} />
                    ) : (
                      <XCircle size={18} color={colors.error} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", pt: "2px" }}>
                    {detail.hasPlacement ? (
                      <CheckCircle2 size={18} color={colors.success} />
                    ) : (
                      <XCircle size={18} color={colors.error} />
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
}
